// -----------------------------------------------------------------------------
// Experience Genius Override service
// -----------------------------------------------------------------------------
// Il backend legacy non persiste in modo affidabile i campi estesi del Genius
// Loci (tutorDescription, tutorPersonality, tutorKnowledge, tutorLanguageRegister,
// tutorEpoch, geminiPrompt, recoveryPrompt, voiceCode, ...). Spesso vengono
// troncati o scartati per DataException. Per garantire persistenza stabile e
// coerenza fra webapp, mobile app e motore AI, salviamo questi campi su
// Firestore tramite Cloud Function dedicata.
//
// Endpoint: /api/experience-genius-override (GET/POST)
// -----------------------------------------------------------------------------

export interface ExperienceGeniusTranslation {
  nomeChatbot?: string;
  tutorDescription?: string;
  tutorPersonality?: string;
  tutorKnowledge?: string;
  tutorLanguageRegister?: string;
  tutorEpoch?: string;
  geminiPrompt?: string;
  recoveryPrompt?: string;
  fallbackMessage?: string;
  welcomeMessage?: string;
  voiceCode?: string;
  voiceName?: string;
  experienceName?: string;
  experienceDescription?: string;
}

export interface ExperienceGeniusTranslations {
  en?: ExperienceGeniusTranslation;
}

export interface ExperienceGeniusOverride {
  tutorDescription?: string;
  tutorPersonality?: string;
  tutorKnowledge?: string;
  tutorLanguageRegister?: string;
  tutorEpoch?: string;
  geminiPrompt?: string;
  recoveryPrompt?: string;
  voiceCode?: string;
  voiceName?: string;
  voce?: number;
  voiceId?: number;
  idPersonality?: number;
  jungArchetype?: string;
  nomeChatbot?: string;
  characterEra?: string;
  characterRole?: string;
  fallbackMessage?: string;
  welcomeMessage?: string;
  responseLength?: 'short' | 'medium' | 'long';
  routePlan?: string;
  translations?: ExperienceGeniusTranslations;
}

export const MIN_EN_TRANSLATABLE_EXPERIENCE_ID = 113;

const isBrowser = typeof window !== 'undefined';

const FN_BASE = (() => {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> })?.env || {};
    const base = String(env.VITE_PLATFORM_API_BASE || env.VITE_FIREBASE_FUNCTIONS_BASE || 'https://platform.glooci.it/api').trim();
    return base.replace(/\/$/, '');
  } catch {
    return 'https://platform.glooci.it/api';
  }
})();

const DIRECT_FN_URL = `${FN_BASE}/experience-genius-override`;

function resolveUrl(experienceId: number | null, suffix = ''): string {
  if (isBrowser) {
    if (Number.isFinite(experienceId) && Number(experienceId) > 0) {
      return `/api/experience-genius-override/${experienceId}${suffix}`;
    }
    return `/api/experience-genius-override${suffix}`;
  }
  const qs = Number.isFinite(experienceId) && Number(experienceId) > 0
    ? `?experienceId=${experienceId}`
    : '';
  return `${DIRECT_FN_URL}${qs}`;
}

function emptyOverride(): ExperienceGeniusOverride {
  return {};
}

const TRANSLATION_PICKS: Array<keyof ExperienceGeniusTranslation> = [
  'nomeChatbot',
  'tutorDescription',
  'tutorPersonality',
  'tutorKnowledge',
  'tutorLanguageRegister',
  'tutorEpoch',
  'geminiPrompt',
  'recoveryPrompt',
  'fallbackMessage',
  'welcomeMessage',
  'voiceCode',
  'voiceName',
  'experienceName',
  'experienceDescription',
];

export const AI_TRANSLATION_PICKS: Array<keyof ExperienceGeniusTranslation> = [
  'experienceName',
  'experienceDescription',
  'welcomeMessage',
  'nomeChatbot',
  'tutorDescription',
  'tutorPersonality',
  'tutorKnowledge',
  'tutorLanguageRegister',
  'tutorEpoch',
];

export function isExperienceEligibleForEnTranslation(
  experienceId: number | null | undefined,
): boolean {
  return Number.isFinite(experienceId)
    && Number(experienceId) >= MIN_EN_TRANSLATABLE_EXPERIENCE_ID;
}

function normalizeTranslation(raw: unknown): ExperienceGeniusTranslation {
  if (!raw || typeof raw !== 'object') return {};
  const src = raw as Record<string, unknown>;
  const out: ExperienceGeniusTranslation = {};
  TRANSLATION_PICKS.forEach((key) => {
    const value = src[key];
    if (value === undefined || value === null) return;
    const str = String(value).trim();
    if (str) (out as Record<string, string>)[key] = str;
  });
  return out;
}

function normalizeTranslations(raw: unknown): ExperienceGeniusTranslations | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const src = raw as Record<string, unknown>;
  const en = normalizeTranslation(src.en);
  const has = Object.keys(en).length > 0;
  return has ? { en } : undefined;
}

function pickPositiveInteger(src: Record<string, unknown>, key: string): number | undefined {
  const value = src[key];
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.trunc(parsed);
}

function normalizeOverride(raw: unknown): ExperienceGeniusOverride {
  if (!raw || typeof raw !== 'object') return emptyOverride();
  const src = raw as Record<string, unknown>;
  const pick = (k: string): string | undefined => {
    const v = src[k];
    if (v === undefined || v === null) return undefined;
    const s = String(v).trim();
    return s ? s : undefined;
  };
  const pickResponseLength = (): 'short' | 'medium' | 'long' | undefined => {
    const rawValue = pick('responseLength');
    return rawValue === 'short' || rawValue === 'medium' || rawValue === 'long'
      ? rawValue
      : undefined;
  };
  const translations = normalizeTranslations(src.translations);
  const out: ExperienceGeniusOverride = {
    tutorDescription: pick('tutorDescription'),
    tutorPersonality: pick('tutorPersonality'),
    tutorKnowledge: pick('tutorKnowledge'),
    tutorLanguageRegister: pick('tutorLanguageRegister'),
    tutorEpoch: pick('tutorEpoch'),
    geminiPrompt: pick('geminiPrompt'),
    recoveryPrompt: pick('recoveryPrompt'),
    voiceCode: pick('voiceCode'),
    voiceName: pick('voiceName'),
    voce: pickPositiveInteger(src, 'voce') ?? pickPositiveInteger(src, 'voiceId') ?? pickPositiveInteger(src, 'voiceCode'),
    voiceId: pickPositiveInteger(src, 'voiceId') ?? pickPositiveInteger(src, 'voce'),
    idPersonality: pickPositiveInteger(src, 'idPersonality'),
    jungArchetype: pick('jungArchetype'),
    nomeChatbot: pick('nomeChatbot'),
    characterEra: pick('characterEra'),
    characterRole: pick('characterRole'),
    fallbackMessage: pick('fallbackMessage'),
    welcomeMessage: pick('welcomeMessage'),
    responseLength: pickResponseLength(),
    routePlan: pick('routePlan'),
  };
  if (translations) out.translations = translations;
  return out;
}

/**
 * Legge l override Genius persistente su Firestore per una esperienza.
 * Ritorna un oggetto vuoto se non esiste.
 */
export async function getExperienceGeniusOverride(
  experienceId: number,
): Promise<ExperienceGeniusOverride> {
  if (!Number.isFinite(experienceId) || Number(experienceId) <= 0) {
    return emptyOverride();
  }
  try {
    const response = await fetch(resolveUrl(experienceId), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return emptyOverride();
    }
    const result = await response.json().catch(() => null);
    if (!result || !result.success) return emptyOverride();
    return normalizeOverride(result.override);
  } catch (error) {
    console.warn('[experienceGeniusOverride] GET error', error);
    return emptyOverride();
  }
}

/**
 * Salva/upserta l override Genius persistente per una esperienza.
 * L override puo contenere uno o piu campi; quelli omessi non verranno
 * sovrascritti (merge = true server-side).
 */
export async function saveExperienceGeniusOverride(
  experienceId: number,
  override: ExperienceGeniusOverride,
): Promise<ExperienceGeniusOverride | null> {
  if (!Number.isFinite(experienceId) || Number(experienceId) <= 0) {
    return null;
  }
  try {
    console.info('[experienceGeniusOverride] POST payload', {
      experienceId,
      fields: Object.keys(override || {}),
      voce: override.voce,
      idPersonality: override.idPersonality,
      jungArchetype: override.jungArchetype,
      voiceCode: override.voiceCode,
    });
    const response = await fetch(resolveUrl(experienceId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        ...override,
        experienceId,
      }),
    });
    if (!response.ok) {
      console.warn('[experienceGeniusOverride] POST failed', response.status);
      return null;
    }
    const result = await response.json().catch(() => null);
    if (!result || !result.success) return null;
    return normalizeOverride(result.override);
  } catch (error) {
    console.warn('[experienceGeniusOverride] POST error', error);
    return null;
  }
}

/**
 * Genera una bozza EN dei campi IT via Gemini. Se `persist=true` e
 * experienceId valido, scrive `translations.en` nel documento Firestore.
 */
export async function translateExperienceToEn(params: {
  experienceId?: number;
  fields: ExperienceGeniusTranslation;
  persist?: boolean;
}): Promise<ExperienceGeniusTranslation | null> {
  const url = isBrowser
    ? '/api/translate-experience-to-en'
    : `${FN_BASE}/translate-experience-to-en`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        experienceId: params.experienceId,
        fields: params.fields,
        persist: Boolean(params.persist),
      }),
    });
    if (!response.ok) {
      console.warn('[translateExperienceToEn] failed', response.status);
      return null;
    }
    const result = await response.json().catch(() => null);
    if (!result || !result.success) return null;
    return normalizeTranslation(result.translated);
  } catch (error) {
    console.warn('[translateExperienceToEn] error', error);
    return null;
  }
}

/**
 * Scrive solo il blocco translations.en su Firestore (senza re-inviare i
 * campi IT). Utile dopo editing manuale dei campi EN nel form.
 */
export async function saveExperienceTranslationEn(
  experienceId: number,
  translation: ExperienceGeniusTranslation,
): Promise<boolean> {
  if (!Number.isFinite(experienceId) || experienceId <= 0) return false;
  try {
    const response = await fetch(resolveUrl(experienceId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        experienceId,
        translations: { en: translation },
      }),
    });
    return response.ok;
  } catch (error) {
    console.warn('[saveExperienceTranslationEn] error', error);
    return false;
  }
}

/**
 * Merge fra un oggetto LegacyExperience e l override Firestore.
 * I campi dell override hanno priorita su quelli legacy (se non vuoti).
 */
export function mergeGeniusOverrideIntoExperience<T extends Record<string, unknown>>(
  experience: T,
  override: ExperienceGeniusOverride | null | undefined,
): T {
  if (!override || typeof override !== 'object') return experience;
  const merged: Record<string, unknown> = { ...experience };
  (Object.keys(override) as Array<keyof ExperienceGeniusOverride>).forEach((key) => {
    const value = override[key];
    if (value === undefined || value === null) return;
    const str = String(value).trim();
    if (!str) return;
    merged[key] = str;
  });
  return merged as T;
}
